<script lang="ts" setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import PresenterValueFallback from '@satellite/components/elements/presenters/PresenterValueFallback.vue';
import { useYardAssetDisplayV2 } from '@satellite/composables/useYardAssetDisplayV2';

import type { YardAssetDetails } from '@satellite/composables/useYardAssetPresenterV2';

const props = defineProps<{
  title: string;
  asset: YardAssetDetails | null;
  testid?: string;
}>();

const { t } = useI18n();
const display = useYardAssetDisplayV2(computed(() => props.asset));

const fields = computed(() => [
  { key: 'id', label: t('checkSpotPanel.labels.assetId'), value: display.code },
  { key: 'type', label: t('checkSpotPanel.labels.assetType'), value: display.typeLabel },
  { key: 'status', label: t('checkSpotPanel.labels.assetStatus'), value: display.status },
  {
    key: 'state',
    label: t('checkSpotPanel.labels.assetState'),
    value: display.state,
    fallback: t('checkSpotPanel.labels.notInformed')
  },
  { key: 'owner', label: t('checkSpotPanel.labels.assetOwner'), value: display.owner?.name ?? null }
]);
</script>

<template>
  <div class="d-flex flex-column gap-3" :data-testid="testid">
    <span class="text-overline text-high-emphasis">{{ title }}</span>
    <v-card
      v-for="field in fields"
      :key="field.key"
      variant="flat"
      border
      :data-testid="testid ? `${testid}-${field.key}` : undefined">
      <v-card-text class="d-flex flex-column gap-1">
        <span class="typography-body-sm-bold">{{ field.label }}</span>
        <span v-if="field.value" class="typography-body-sm text-medium-emphasis">
          {{ field.value }}
        </span>
        <presenter-value-fallback v-else :text="field.fallback" class="typography-body-sm" />
      </v-card-text>
    </v-card>
  </div>
</template>
